import { FunctionComponent, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useForm, ValidationMode } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import CardFormSchema from './CardFormValidationSchema';
import { CardInfoFields } from './CardFormTypes';
import { sendMessage, handleTokenizationMessage, CardEventTypes } from '../PostMessageHelpers';
import * as Swipe from 'card-swipe';
import { CardFormMultiLine } from './CardFormMultiLine';
import { CardFormSingleLine } from './CardFormSingleLine';

const CardForm: FunctionComponent = () => {
  let scanner = new Swipe.Scanner();
  const location = useLocation();
  const formRef = useRef<HTMLFormElement>(null);
  const searchParams = new URLSearchParams(location.search);
  const validationModeParam = searchParams.get('validationMode');
  const singleLineForm = searchParams.get('singleLine');
  const defaultValidationMode: keyof ValidationMode = 'onSubmit';
  const validationMode = (validationModeParam || defaultValidationMode) as keyof ValidationMode;
  const [styleOverrides, setStyleOverrides] = useState({});
  const {
    register,
    handleSubmit,
    formState: { errors },
    trigger,
    setValue,
    setFocus,
    watch } = useForm({
      defaultValues: { ...new CardInfoFields() },
      resolver: yupResolver(CardFormSchema),
      mode: validationMode
    });

  useEffect(() => {
    window.addEventListener('message', handleMessageReceived);
    window.addEventListener('keydown', handleScannerInput);
    new Swipe({
      scanner: scanner,
      onScan: (swipeData: any) => { handleSwipeData(swipeData); }
    });

    sendMessage({ eventType: CardEventTypes.ready });

    return () => {
      window.removeEventListener('message', handleMessageReceived);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function sendHeightMessage() {
    const height = formRef.current?.clientHeight;
    if (!height) return;
    sendMessage({
      eventType: CardEventTypes.resize,
      data: { height: height }
    });
  };

  useLayoutEffect(() => {
    sendHeightMessage();
  });

  async function handleMessageReceived(event: MessageEvent) {
    if (event.data.eventType === CardEventTypes.validate) {
      const isValid = await trigger();
      sendMessage({ eventType: CardEventTypes.validate, data: { isValid: isValid } });
    }
    if (event.data.eventType === CardEventTypes.tokenize) {
      handleSubmit(
        (formData) => handleValidFormSubmit(event, formData),
        (errors) => handleInvalidFormSubmit(errors)
      )();
    }
    if (event.data.eventType === CardEventTypes.styleOverrides) {
      setStyleOverrides(event.data.styleOverrides);
    }
    if (event.data.eventType === CardEventTypes.resize) {
      sendHeightMessage();
    }
  }

  function handleValidFormSubmit(event: MessageEvent, formData: CardInfoFields) {
    handleTokenizationMessage(event.data, formData);
  }

  function handleInvalidFormSubmit(errors: any) {
    // console.log(errors);
  }

  function handleScannerInput(e: any) {
    if (e.key.length === 1) scanner.input(e.key);
  }

  async function handleSwipeData(swipeData: any)  {
    setValue('number', swipeData.account);
    setValue('month', swipeData.exp_month);
    setValue('year', swipeData.exp_year);
    setTimeout(() => {
      setFocus('verification');
    }, 50);
  }

  function getFormControlClassName(fieldName: keyof CardInfoFields) {
    const hasError = errors[fieldName]?.message;
    const formControlClass = 'form-control';
    const validityClass = hasError ? 'error' : '';
    return [formControlClass, validityClass].join(' ');
  }

  return (
    <>
      {singleLineForm ?
        <CardFormSingleLine
          styleOverrides={styleOverrides}
          formRef={formRef}
          register={register}
          watch={watch}
          errors={errors} />
        :
        <CardFormMultiLine
          styleOverrides={styleOverrides}
          getFormControlClassName={getFormControlClassName}
          formRef={formRef}
          register={register}
          watch={watch}
          errors={errors} />}
    </>
  );
};

export default CardForm;
